import { create } from 'zustand';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { PositionedItem } from '../../CustomDash/components/PageConfigProvider/types/item';
import type { EntityType } from '../../CustomDash/utils/routing-helpers';
import type { ChartDataDigest } from '../turn-outcome-digest';

/**
 * A pending headless chart-data fetch. The offscreen host
 * (`global-ai-headless-digest-host.tsx`) mounts one provider tree per request
 * — the SAME ChartRuntimeShell + AI filter scope + data-provider stack a
 * visible assistant chart uses, minus every visual concern — and resolves the
 * request with the settled digest. Consistency with the dashboard is by
 * construction: there is no second data path to drift.
 */
export type HeadlessDigestRequest = {
  requestId: string;
  /** Chart-shape item (validated upstream via getValidatedPositionedItemsFromLlmContent). */
  item: PositionedItem;
  /** search_filters artifact; undefined → the synthetic page's unfiltered defaults. */
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  entityType: EntityType;
};

type HeadlessDigestStoreState = {
  requests: readonly HeadlessDigestRequest[];
  enqueue: (request: HeadlessDigestRequest) => void;
  remove: (requestId: string) => void;
};

export const useHeadlessDigestStore = create<HeadlessDigestStoreState>()(
  (set) => ({
    requests: [],
    enqueue: (request) => set((s) => ({ requests: [...s.requests, request] })),
    remove: (requestId) =>
      set((s) => ({
        requests: s.requests.filter((r) => r.requestId !== requestId),
      })),
  })
);

/** Resolvers live outside the store: functions don't belong in zustand state. */
const resolvers = new Map<string, (digest: ChartDataDigest) => void>();

/**
 * Resolve a pending request with its settled digest. Called by the host tree
 * (provider success/error, filter-apply failure) and by the fetch's own
 * timeout/abort. Idempotent — late or duplicate reports after resolution are
 * dropped, and removal from the store unmounts the request's provider tree.
 */
export function resolveHeadlessDigest(
  requestId: string,
  digest: ChartDataDigest
): void {
  const resolve = resolvers.get(requestId);
  if (!resolve) return;
  resolvers.delete(requestId);
  useHeadlessDigestStore.getState().remove(requestId);
  resolve(digest);
}

function syntheticEmptyDigest(
  item: PositionedItem,
  error?: true
): ChartDataDigest {
  return {
    kind: 'chart_data',
    metric: item.plot?.defaultMetricOptions?.[0] ?? 'chart',
    chartType: String(item.plot?.chartType ?? 'unknown'),
    rowCount: 0,
    empty: true,
    ...(error ? { error: true as const } : {}),
  };
}

export const HEADLESS_DIGEST_TIMEOUT_MS = 30_000;

/**
 * Fetch a chart-data digest without mounting any chart UI. Resolves with the
 * settled digest, or a synthetic EMPTY digest on timeout/abort — mirroring the
 * legacy `awaitChartSettle` contract (timeout is empty, data-layer failure is
 * `error: true`) so digest consumers need no path-specific handling.
 */
export function fetchHeadlessChartDigest(
  input: Omit<HeadlessDigestRequest, 'requestId'>,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
): Promise<ChartDataDigest> {
  recordHeadlessInputForDevProbe(input);
  const requestId = crypto.randomUUID();
  return new Promise<ChartDataDigest>((resolve) => {
    const timeoutMs = opts?.timeoutMs ?? HEADLESS_DIGEST_TIMEOUT_MS;
    const timer = setTimeout(() => {
      resolveHeadlessDigest(requestId, syntheticEmptyDigest(input.item));
    }, timeoutMs);
    const onAbort = () => {
      resolveHeadlessDigest(requestId, syntheticEmptyDigest(input.item));
    };
    opts?.signal?.addEventListener('abort', onAbort, { once: true });
    resolvers.set(requestId, (digest) => {
      clearTimeout(timer);
      opts?.signal?.removeEventListener('abort', onAbort);
      resolve(digest);
    });
    useHeadlessDigestStore.getState().enqueue({ requestId, ...input });
  });
}

let warmUpFired = false;

/**
 * Fire-and-forget cache warm-up for the headless digest path. The FIRST
 * provider mount of a session pays for selection lists, taxonomy trees, and
 * preference loads (~4-5s measured) — costs every later fetch skips via the
 * app-level caches. Mounting one throwaway unfiltered headcount fetch when
 * the drawer opens moves that tax off the user's first question. Idempotent
 * per session; the result is discarded.
 */
export function warmHeadlessDigestPath(): void {
  if (warmUpFired) return;
  warmUpFired = true;
  void fetchHeadlessChartDigest(
    {
      item: {
        id: 'ga-headless-warm-up',
        title: 'Headcount',
        position: { x: 0, y: 0, width: 48, height: 22 },
        plot: { chartType: 'bar', defaultMetricOptions: ['headcount'] },
      },
      filters: undefined,
      entityType: 'company',
    },
    { timeoutMs: 15_000 }
  );
}

declare global {
  interface Window {
    __gaFetchHeadlessDigest?: typeof fetchHeadlessChartDigest;
    __gaLastHeadlessInput?: Omit<HeadlessDigestRequest, 'requestId'>;
  }
}

// Dev probe: lets a console session fetch a digest directly, e.g. to parity-
// check a figure against the drawer's answer. Never present in production.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__gaFetchHeadlessDigest = fetchHeadlessChartDigest;
}

/**
 * Dev aid: stash the latest real request's input so a console probe can reuse
 * its resolved filters artifact (the unfiltered-defaults path is not
 * guaranteed to settle on every backend env; real flows always carry an
 * artifact). No-op in production builds.
 */
export function recordHeadlessInputForDevProbe(
  input: Omit<HeadlessDigestRequest, 'requestId'>
): void {
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.__gaLastHeadlessInput = input;
  }
}
